import React, { useState } from 'react';
import { Card, Upload, message, Space, Typography, Progress, Button, Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox, faTrash } from '@fortawesome/free-solid-svg-icons';
import { callAnalyticsAPI } from '../../../services/callAnalytics';

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

const CSVImportCard = ({ title, type, onSuccess, description, acceptedFormats }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastImport, setLastImport] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleUpload = async (file) => {
    if (!file.name.endsWith('.csv')) {
      message.error('Please upload a CSV file');
      return false;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      let result;
      if (type === 'ringcentral') {
        result = await callAnalyticsAPI.importRingCentral(file);
      } else if (type === 'google-ads') {
        result = await callAnalyticsAPI.importGoogleAds(file);
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      setLastImport({
        fileName: file.name,
        timestamp: new Date(),
        total: result.total,
        imported: result.imported,
        updated: result.updated || 0,
        duplicates: result.duplicates || 0,
        skipped: result.skipped,
        errors: result.errors || []
      });

      // Show appropriate message based on results
      if (result.errors && result.errors.length > 0) {
        message.warning(`Imported with ${result.errors.length} errors. Check console for details.`);
        console.error('Import errors:', result.errors);
      } else if (result.updated > 0 && result.duplicates > 0) {
        message.success(`Imported ${result.imported} new, updated ${result.updated} incomplete records. Skipped ${result.duplicates} duplicates.`);
      } else if (result.updated > 0) {
        message.success(`Imported ${result.imported} new records, updated ${result.updated} incomplete records.`);
      } else if (result.duplicates > 0) {
        message.warning(`Imported ${result.imported} new records. Skipped ${result.duplicates} duplicates (already in database).`);
      } else {
        message.success(`Successfully imported ${result.imported} records`);
      }

      if (onSuccess) {
        onSuccess(result);
      }

      // Reset progress after a delay
      setTimeout(() => {
        setUploadProgress(0);
      }, 2000);

    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to import file');
      console.error('Import error:', error);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      let result;
      if (type === 'ringcentral') {
        result = await callAnalyticsAPI.deleteRingCentralData();
      } else if (type === 'google-ads') {
        result = await callAnalyticsAPI.deleteGoogleAdsData();
      }

      message.success(result.message || `Deleted ${result.deletedCount} records`);
      setLastImport(null);
      
      if (onSuccess) {
        onSuccess({ deleted: true, count: result.deletedCount });
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete data');
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: acceptedFormats,
    beforeUpload: handleUpload,
    showUploadList: false
  };

  return (
    <Card 
      title={title}
      size="small"
      extra={
        <Popconfirm
          title="Delete all data?"
          description={`Are you sure you want to delete all ${type === 'ringcentral' ? 'RingCentral' : 'Google Ads'} data? This cannot be undone.`}
          onConfirm={handleDelete}
          okText="Yes, delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button 
            type="text" 
            danger 
            icon={<FontAwesomeIcon icon={faTrash} />}
            loading={deleting}
            disabled={uploading}
          >
            Clear Data
          </Button>
        </Popconfirm>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {description}
        </Paragraph>

        <Dragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <FontAwesomeIcon icon={faInbox} />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
          <p className="ant-upload-hint">
            Supports {acceptedFormats} format only
          </p>
        </Dragger>

        {uploading && (
          <Progress 
            percent={uploadProgress} 
            status="active"
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
        )}

        {lastImport && (
          <div style={{ 
            padding: '12px', 
            background: '#f5f5f5', 
            borderRadius: '4px' 
          }}>
            <Text strong>Last Import:</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {lastImport.fileName}
            </Text>
            <br />
            <Space size="large" style={{ marginTop: '8px' }}>
              <Text>
                <span style={{ color: '#52c41a' }}>✓ {lastImport.imported || 0}</span> imported
              </Text>
              {lastImport.updated > 0 && (
                <Text>
                  <span style={{ color: '#13c2c2' }}>↻ {lastImport.updated}</span> updated
                </Text>
              )}
              {lastImport.duplicates > 0 && (
                <Text>
                  <span style={{ color: '#1890ff' }}>⊙ {lastImport.duplicates}</span> duplicates
                </Text>
              )}
              {(lastImport.skipped - (lastImport.duplicates || 0)) > 0 && (
                <Text>
                  <span style={{ color: '#faad14' }}>⊘ {lastImport.skipped - (lastImport.duplicates || 0)}</span> skipped
                </Text>
              )}
              {lastImport.errors && lastImport.errors.length > 0 && (
                <Text>
                  <span style={{ color: '#ff4d4f' }}>✗ {lastImport.errors.length}</span> errors
                </Text>
              )}
            </Space>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default CSVImportCard;
